// Build an ETC Eos-import CSV directly from Cuelist cue data.
//
// Format reverse-engineered from a real Eos console export (37-column
// START_TARGETS block). Cue parts are encoded as a parent row (blank
// PART_NUMBER, cue-level fields) plus one child row per part sharing the cue's
// TARGET_ID and TARGET_DCID, with PART_NUMBER = 1..N and its own times.

// The exact 37-column header, in order, from the console export.
export const EOS_HEADER = [
    'TARGET_TYPE', 'TARGET_TYPE_AS_TEXT', 'TARGET_LIST_NUMBER', 'TARGET_ID', 'TARGET_DCID',
    'PART_NUMBER', 'LABEL', 'TIME_DATA', 'UP_DELAY', 'DOWN_TIME', 'DOWN_DELAY', 'FOCUS_TIME',
    'FOCUS_DELAY', 'COLOR_TIME', 'COLOR_DELAY', 'BEAM_TIME', 'BEAM_DELAY', 'DURATION',
    'ALERT_TIME', 'MARK', 'BLOCK', 'ASSERT', 'ALL_FADE', 'PREHEAT', 'FOLLOW', 'LINK', 'LOOP',
    'CURVE', 'RATE', 'EXTERNAL_LINKS', 'EFFECTS', 'MODE', 'CUE_NOTES', 'SCENE_TEXT', 'SCENE_END',
    'WIDTH', 'HEIGHT'
];

// The literal SCENE_END flag, confirmed 2026-08-16 against a console export from a
// show built with `[Cue] 5 [Thru] 9 {Attribute} {Scene} Act One` — Eos writes the
// string "Scene_End" on the cue that ENDS the section (cue 9), not a boolean.
export const EOS_SCENE_END_VALUE = 'Scene_End';

const VALID = '[\\d.:]+'; // a time value: digits, dots, colons
const SINGLE_RE = new RegExp('^(?:\\((' + VALID + ')\\))?(' + VALID + ')$');

// mm:ss -> m*60+s, hh:mm:ss -> h*3600+m*60+s, else float. Blank/NaN -> null.
export function timeToSeconds(val) {
    if (val == null) return null;
    const s = String(val).trim();
    if (s === '' || s.toLowerCase() === 'nan') return null;
    if (s.indexOf(':') !== -1) {
        const parts = s.split(':').map(p => parseFloat(p));
        if (parts.some(n => Number.isNaN(n))) return null;
        if (parts.length === 2) return (parts[0] * 60) + parts[1];
        if (parts.length === 3) return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
        return null;
    }
    const n = parseFloat(s);
    return Number.isNaN(n) ? null : n;
}

// "(delay)value" -> { delay, time } (each converted to seconds); missing -> null.
export function splitDelayTime(val) {
    const s = String(val == null ? '' : val).replace(/\s+/g, '');
    if (s === '') return { delay: null, time: null };
    const m = s.match(SINGLE_RE);
    if (!m) return { delay: null, time: null };
    return { delay: timeToSeconds(m[1]), time: timeToSeconds(m[2]) };
}

// Parse an up/down time string "(d)up/(d)down" (single value = up only).
// Blank down mirrors up (time + delay), matching the reference converter.
export function parseUpDown(val) {
    const s = String(val == null ? '' : val).replace(/\s+/g, '');
    let upDelay = null;
    let upTime = null;
    let downDelay = null;
    let downTime = null;
    if (s !== '') {
        if (s.indexOf('/') !== -1) {
            const idx = s.indexOf('/');
            const up = splitDelayTime(s.slice(0, idx));
            const down = splitDelayTime(s.slice(idx + 1));
            upDelay = up.delay; upTime = up.time;
            downDelay = down.delay; downTime = down.time;
        } else {
            const up = splitDelayTime(s);
            upDelay = up.delay; upTime = up.time;
        }
    }
    if (downTime == null && downDelay == null) {
        downTime = upTime;
        downDelay = upDelay;
    }
    return { upDelay, upTime, downDelay, downTime };
}

export function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    // v4 fallback
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        const v = c === 'x' ? r : (8 + (r % 4)); // y digit is 8..b
        return v.toString(16);
    });
}

function num(v) {
    return v == null ? '' : String(v);
}

// One Eos row object, keyed by EOS_HEADER names. Fixed cue-row constants baked in.
function blankRow(targetId, dcid, partNumber) {
    const row = {};
    for (let i = 0; i < EOS_HEADER.length; i += 1) row[EOS_HEADER[i]] = '';
    row.TARGET_TYPE = '1';
    row.TARGET_TYPE_AS_TEXT = 'Cue';
    row.TARGET_LIST_NUMBER = '1';
    row.TARGET_ID = targetId == null ? '' : String(targetId);
    row.TARGET_DCID = dcid;
    row.PART_NUMBER = partNumber == null ? '' : String(partNumber);
    return row;
}

// Fill a row's time columns from an up/down string. Returns whether any time was set.
function applyTime(row, timeStr) {
    const t = parseUpDown(timeStr);
    row.TIME_DATA = num(t.upTime);
    row.UP_DELAY = num(t.upDelay);
    row.DOWN_TIME = num(t.downTime);
    row.DOWN_DELAY = num(t.downDelay);
    return t.upTime != null || t.upDelay != null || t.downTime != null || t.downDelay != null;
}

function applyDelayCol(row, timeCol, delayCol, val) {
    const dt = splitDelayTime(val);
    row[timeCol] = num(dt.time);
    row[delayCol] = num(dt.delay);
}

// The cue-level (non-part) fields shared by a whole cue / a parts parent row.
function applyCueLevel(row, cue) {
    const d = cue.data || {};
    row.LABEL = d.label == null ? '' : String(d.label);
    row.BLOCK = d.block == null ? '' : String(d.block);
    row.ASSERT = d.assert == null ? '' : String(d.assert);
    row.FOLLOW = (d.followHangType || '') + (d.followHang || '');
    row.LINK = d.link == null ? '' : String(d.link);
    row.LOOP = d.loop == null ? '' : String(d.loop);
    row.CURVE = d.curve == null ? '' : String(d.curve);
    row.EXTERNAL_LINKS = d.externalLinks == null ? '' : String(d.externalLinks);
    row.CUE_NOTES = d.notes == null ? '' : String(d.notes);
}

// Structure section columns for a cue-level row. Blank unless the caller supplied
// a section for this cue, so an unflagged export is unchanged.
function applyScene(row, cue, sceneStarts, sceneEnds) {
    const id = cue && cue.id;
    if (id == null) return;
    const text = sceneStarts[id];
    if (text) {
        row.SCENE_TEXT = String(text);
    }
    if (sceneEnds[id]) {
        row.SCENE_END = EOS_SCENE_END_VALUE;
    }
}

// Build Eos row objects for the given cues.
//
// opts.defaultTime   fills timeless rows.
// opts.sceneStarts   { [cueId]: 'Act 2 - Scene 1' } -> SCENE_TEXT. An Eos scene is
//                    a green bar ABOVE its cue carrying the text, so the label goes
//                    on the cue that STARTS the section.
// opts.sceneEnds     { [cueId]: true } -> SCENE_END, the bar BELOW its cue.
//
// Both default to {}, so callers that pass neither get exactly the previous
// output. This module stays a pure field mapper: the document ordering that
// produces those maps lives in structureOrder.js.
export function buildEosRows(cues, opts) {
    const defaultTime = opts && opts.defaultTime != null ? String(opts.defaultTime) : '';
    const sceneStarts = (opts && opts.sceneStarts) || {};
    const sceneEnds = (opts && opts.sceneEnds) || {};
    const rows = [];
    for (const cue of cues) {
        if (!cue) continue; // eslint-disable-line no-continue
        const d = cue.data || {};
        const dcid = uuid();
        const hasParts = d.parts && Array.isArray(d.partsData) && d.partsData.length;
        if (hasParts) {
            // Parent row: cue-level fields, blank part number + time.
            const parent = blankRow(cue.title, dcid, '');
            applyCueLevel(parent, cue);
            // Scene columns are cue-level, so they belong on the parent row only —
            // the parent IS the cue in Eos, same as LABEL / BLOCK / FOLLOW.
            applyScene(parent, cue, sceneStarts, sceneEnds);
            if (d.mark != null && d.mark !== '') parent.MARK = String(d.mark);
            rows.push(parent);
            // Child row per part.
            for (let i = 0; i < d.partsData.length; i += 1) {
                const part = d.partsData[i] || {};
                const partNo = part.id != null && part.id !== '' ? part.id : (i + 1);
                const child = blankRow(cue.title, dcid, partNo);
                child.LABEL = part.label == null ? '' : String(part.label);
                const hasTime = applyTime(child, part.time);
                applyDelayCol(child, 'FOCUS_TIME', 'FOCUS_DELAY', part.focus);
                applyDelayCol(child, 'COLOR_TIME', 'COLOR_DELAY', part.color);
                applyDelayCol(child, 'BEAM_TIME', 'BEAM_DELAY', part.beam);
                child.MARK = part.mark == null ? '' : String(part.mark);
                if (!hasTime && defaultTime !== '') child.TIME_DATA = defaultTime;
                rows.push(child);
            }
        } else {
            const row = blankRow(cue.title, dcid, '');
            applyCueLevel(row, cue);
            applyScene(row, cue, sceneStarts, sceneEnds);
            const hasTime = applyTime(row, d.time);
            applyDelayCol(row, 'FOCUS_TIME', 'FOCUS_DELAY', d.focus);
            applyDelayCol(row, 'COLOR_TIME', 'COLOR_DELAY', d.color);
            applyDelayCol(row, 'BEAM_TIME', 'BEAM_DELAY', d.beam);
            row.MARK = d.mark == null ? '' : String(d.mark);
            if (!hasTime && defaultTime !== '') row.TIME_DATA = defaultTime;
            rows.push(row);
        }
    }
    return rows;
}

function csvCell(v) {
    const s = v == null ? '' : String(v);
    return '"' + s.replace(/"/g, '""') + '"';
}

// Assemble the full Eos CSV text (caller prepends the UTF-8 BOM).
export function toEosCsv(rows) {
    const lines = ['START_TARGETS'];
    lines.push(EOS_HEADER.join(','));
    for (let i = 0; i < rows.length; i += 1) {
        lines.push(EOS_HEADER.map(col => csvCell(rows[i][col])).join(','));
    }
    lines.push('END_TARGETS');
    return lines.join('\n');
}



// WEBPACK FOOTER //
// ./src/lib/eosExport.js